import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

function TreeRequest({ request, selectedRequestKey, onSelectRequest }) {
  const isSelected = selectedRequestKey === request.uniqueKey;

  return (
    <ListItemButton
      className="lpt-tree-request"
      selected={isSelected}
      onClick={() => onSelectRequest(request)}
    >
      <span className="lpt-tree-method-badge">{request.method}</span>
      <ListItemText
        primary={request.name}
        secondary={request.url}
        primaryTypographyProps={{ fontSize: 13 }}
        secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
      />
    </ListItemButton>
  );
}

function TreeFile({ file, selectedRequestKey, onCreateRequest, onSelectRequest }) {
  const [open, setOpen] = useState(true);

  return (
    <>
      <ListItemButton
        className="lpt-tree-node"
        onClick={() => setOpen(!open)}
        onContextMenu={(event) => {
          event.preventDefault();
          onCreateRequest(file);
        }}
      >
        <span className="lpt-tree-toggle">{open ? "-" : "+"}</span>
        <span className="lpt-tree-icon lpt-tree-icon-file" aria-hidden="true" />
        <ListItemText
          primary={file.name}
          secondary={
            file.parseError
              ? `Invalid JSON: ${file.parseError}`
              : `${file.requests.length} request(s)`
          }
          primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
          secondaryTypographyProps={{ fontSize: 11 }}
        />
        <IconButton
          aria-label={`Add request to ${file.name}`}
          className="lpt-tree-add-button"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onCreateRequest(file);
          }}
        >
          +
        </IconButton>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding className="lpt-tree-children">
          {file.requests.map((request) => (
            <TreeRequest
              key={`${file.relativePath}-${request.uniqueKey}`}
              request={request}
              selectedRequestKey={selectedRequestKey}
              onSelectRequest={onSelectRequest}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

function TreeFolder({
  folder,
  selectedRequestKey,
  onCreateRequest,
  onSelectRequest,
}) {
  const [open, setOpen] = useState(true);

  return (
    <>
      <ListItemButton className="lpt-tree-node" onClick={() => setOpen(!open)}>
        <span className="lpt-tree-toggle">{open ? "-" : "+"}</span>
        <span className="lpt-tree-icon lpt-tree-icon-folder" aria-hidden="true" />
        <ListItemText
          primary={folder.name}
          secondary={`${folder.files.length} file(s)`}
          primaryTypographyProps={{ fontSize: 14, fontWeight: 700 }}
          secondaryTypographyProps={{ fontSize: 11 }}
        />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding className="lpt-tree-children">
          {folder.files.map((file) => (
            <TreeFile
              key={file.relativePath}
              file={file}
              selectedRequestKey={selectedRequestKey}
              onCreateRequest={onCreateRequest}
              onSelectRequest={onSelectRequest}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

function RestManStudioTree({
  tree,
  treeError,
  isLoadingTree,
  selectedRequestKey,
  onCreateRequest,
  onRefresh,
  onSelectRequest,
}) {
  const folders = tree?.root?.folders || [];
  const [dialogFile, setDialogFile] = useState(null);
  const [newRequestMethod, setNewRequestMethod] = useState("GET");
  const [newRequestName, setNewRequestName] = useState("");

  const openCreateDialog = (file) => {
    const defaultName = `${newRequestMethod}_new_request`;
    setDialogFile(file);
    setNewRequestName(defaultName);
  };

  const closeCreateDialog = () => {
    setDialogFile(null);
  };

  const handleCreateRequest = async () => {
    if (!dialogFile) {
      return;
    }

    await onCreateRequest(dialogFile, {
      method: newRequestMethod,
      requestName: newRequestName,
    });
    closeCreateDialog();
  };

  return (
    <aside className="lpt-tree-panel">
      <Box className="lpt-tree-header">
        <Typography variant="h2">Collections</Typography>
        <Button size="small" variant="outlined" onClick={onRefresh}>
          Refresh
        </Button>
      </Box>
      {isLoadingTree && (
        <Box className="lpt-tree-loading">
          <CircularProgress size={18} />
          <span>Loading</span>
        </Box>
      )}
      {treeError && <p className="lpt-tree-error">{treeError}</p>}
      {!isLoadingTree && !folders.length && (
        <p className="lpt-tree-empty">No saved collections yet.</p>
      )}
      <List dense disablePadding>
        {folders.map((folder) => (
          <TreeFolder
            key={folder.relativePath}
            folder={folder}
            selectedRequestKey={selectedRequestKey}
            onCreateRequest={openCreateDialog}
            onSelectRequest={onSelectRequest}
          />
        ))}
      </List>
      <Dialog open={Boolean(dialogFile)} onClose={closeCreateDialog} fullWidth>
        <DialogTitle>Add Request</DialogTitle>
        <DialogContent className="lpt-tree-dialog">
          <TextField
            select
            label="Method"
            value={newRequestMethod}
            onChange={(event) => setNewRequestMethod(event.target.value)}
            size="small"
            fullWidth
          >
            {["GET", "POST", "PUT", "PATCH", "DELETE"].map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="REST call name"
            value={newRequestName}
            onChange={(event) => setNewRequestName(event.target.value)}
            size="small"
            fullWidth
          />
          <Typography variant="body2" color="text.secondary">
            File: {dialogFile?.name}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateRequest}>
            Add Request
          </Button>
        </DialogActions>
      </Dialog>
    </aside>
  );
}

export default RestManStudioTree;
